import classNames from 'classnames';
import Immutable, { fromJS } from 'immutable';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import AddImageItem from 'components/AddImageItem';
import ImageBox from 'components/ImageBox';
import SelectItem from 'components/SelectItem';
import AssetAlignment from 'components/Toolbar/Controls/AssetAlignment/List';
import { BackgroundImageType } from 'const';
import { imagesForAssetsPanel as imagesForAssetsPanelSelector } from 'containers/Documents/selectors';
import { documentIdsAreBeingUploaded as documentIdsAreBeingUploadedSelector } from 'containers/Project/selectors';
import { createBackgroundImage } from 'factories/style/backgroundImage';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import { BackgroundImageProps } from './models';
import styles from './styles.module.scss';

const TextByBackgroundImageType: Record<BackgroundImageType, () => string> = {
  [BackgroundImageType.COVER]: () => intlGet('EditorToolbar.BackgroundImage.Type', 'Cover'),
  [BackgroundImageType.FILL]: () => intlGet('EditorToolbar.BackgroundImage.Type', 'Fill'),
  [BackgroundImageType.FIT]: () => intlGet('EditorToolbar.BackgroundImage.Type', 'Fit'),
  [BackgroundImageType.ORIGINAL_SIZE]: () => intlGet('EditorToolbar.BackgroundImage.Type', 'OriginalSize'),
  [BackgroundImageType.TILE]: () => intlGet('EditorToolbar.BackgroundImage.Type', 'Tile'),
};

const backgroundTypesList = Object.values(BackgroundImageType);

const BackgroundImage: React.FunctionComponent<BackgroundImageProps> = (props) => {
  const documentIdsAreBeingUploaded = useSelector(documentIdsAreBeingUploadedSelector);
  const images = useSelector(imagesForAssetsPanelSelector);
  const { toggleBackgroundImage, className, controlState, dataTestId = 'backgroundImage' } = props;
  const backgroundImage = props.backgroundImage || fromJS({ alignment : {} }) as Models.BackgroundImageMap;
  const backgroundImageId = backgroundImage.get('id');
  const backgroundImageType = backgroundImage.get('type');
  const backgroundImageAlignment = backgroundImage.get('alignment');
  const isImageBeingUploaded = backgroundImageId ? documentIdsAreBeingUploaded.has(backgroundImageId) : false;

  const deleteBackgroundImage = useCallback(
    (): void => toggleBackgroundImage(Immutable.fromJS(createBackgroundImage()) as Models.BackgroundImageMap),
    [toggleBackgroundImage],
  );

  const setBackgroundImage = useCallback(
    (imageId: string): void => toggleBackgroundImage(backgroundImage.set('id', imageId)),
    [backgroundImage, toggleBackgroundImage],
  );

  const toggleAlignment = useCallback(
    (alignment: Models.AssetAlignmentMap): void => toggleBackgroundImage(backgroundImage.set('alignment', alignment)),
    [backgroundImage, toggleBackgroundImage],
  );

  const changeBackgroundImageType = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    event => toggleBackgroundImage(backgroundImage.set('type', event.target.value as BackgroundImageType)),
    [backgroundImage, toggleBackgroundImage],
  );
  const backgroundImageTextPath = 'EditorToolbar.BackgroundImage';

  return (
    <div className={classNames(styles.BackgroundImage, className)} data-testid={dataTestId}>
      <div className={styles.title} data-testid={`${dataTestId}-title`}>{intlGet(backgroundImageTextPath, 'Title')}</div>
      <div className={styles.menu} data-testid={`${dataTestId}-menu`}>
        {backgroundImageId
          ?
          <>
            <ImageBox
              deleteImage={deleteBackgroundImage}
              image={images.get(backgroundImageId)}
              isImageBeingUploaded={isImageBeingUploaded}
            />
            <div className={styles.controls} data-testid={`${dataTestId}-menu-controls`}>
              <div className={styles.controlTitle} data-testid={`${dataTestId}-menu-controls-title-scale`}>
                {intlGet(backgroundImageTextPath, 'ImageScale')}
              </div>
              <SelectItem
                className={styles.select}
                onChange={changeBackgroundImageType}
                textByValue={TextByBackgroundImageType}
                value={backgroundImageType}
                values={backgroundTypesList}
                dataTestId={`${dataTestId}-menu-controls-selectScale`}
              />
              <div className={styles.controlTitle} data-testid={`${dataTestId}-menu-controls-title-position`}>
                {intlGet(backgroundImageTextPath, 'Position')}
              </div>
              <div className={styles.positions} data-testid={`${dataTestId}-menu-controls-positions`}>
                <AssetAlignment alignment={backgroundImageAlignment} toggleAlignment={toggleAlignment} controlState={controlState} />
              </div>
            </div>
          </>
          :
          <div className={styles.placeholder} data-testid={`${dataTestId}-menu-placeholder`}>
            <div className={styles.placeholderImage} data-testid={`${dataTestId}-menu-placeholder-image`}/>
            <div className={styles.placeholderName} data-testid={`${dataTestId}-menu-placeholder-name`}>
              {intlGet(backgroundImageTextPath, 'ImageName')}
            </div>
          </div>
        }
      </div>
      <div className={styles.title} data-testid={`${dataTestId}-imagesTitle`}>{intlGet(backgroundImageTextPath, 'Images')}</div>
      <ul className={styles.list} data-testid={`${dataTestId}-imagesList`}>
        <AddImageItem dataTestId={`${dataTestId}-addImage`}/>
        {images.valueSeq().map((image, index) => {
          const imageId = image?.get('id');

          return imageId && image && (
            <li className={styles.item} key={imageId} data-testid={`${dataTestId}-imagesList-item${index ?? imageId}`}>
              <ImageBox
                image={image}
                isActive={backgroundImageId === imageId}
                isImageBeingUploaded={documentIdsAreBeingUploaded.has(imageId)}
                onImageClick={setBackgroundImage}
                dataTestId={`${dataTestId}-imagesList-item${index ?? imageId}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default BackgroundImage;
